import { createTheme, type ThemeOptions } from "@mui/material/styles";

// Design tokens sourced from the connected Figma file (Essilor - Dashboard >
// State UI Admin > Login frame) via figwright — see the login page design
// spec. Centralizing here so MUI components pick up the same black-based
// palette and Noto Sans typography app-wide instead of per-component overrides.
export const tokens = {
  color: {
    black: "#111111",
    blackHover: "#000000",
    white: "#FFFFFF",
    textPrimary: "#25272C",
    textMuted: "#8E95A2",
    border: "#D8DBDF",
    link: "#0054FC",
    // The asterisk after a required label (shared/RequiredMark.tsx). Same red
    // as the error toast text, named for the role so the two can diverge.
    required: "#BE2217",
    // Row/menu-item hover. The Figma action dropdown (node 12266:87611) draws
    // its Hover state as a solid Gray/100 fill; MUI's default is a translucent
    // rgba(0,0,0,0.04-0.12) wash, which reads grey-blue over white and darker
    // over a tinted row. Named for the role, not the swatch — several unrelated
    // tokens happen to share this hex.
    hover: "#EDEEF1",
    // Checked checkboxes. Specified directly by the user, and deliberately NOT
    // `black`: on a dense list a blue tick is findable at a glance where a black
    // one merges into the label text beside it.
    checkbox: "#1976D2",
    // Toast (<Snackbar> component set, node 223:30101 in the Figma library)
    // uses a pale-tint "Filled" alert style for every severity — none of the
    // four match MUI's stock filled colors, so all are overridden here.
    toast: {
      error: { background: "#FFE3E1", text: "#BE2217" },
      warning: { background: "#FFFBC5", text: "#BB6902" },
      info: { background: "#D4EDFF", text: "#0054FC" },
      success: { background: "#E3F5E3", text: "#2D6830" },
    },
    // Dashboard stat cards (Figma "Refactor - 08/07/2026" > Dashboard frame,
    // node 12266:8285) — header background, icon chip background, and
    // icon/title color per status, plus the shared body value/label colors.
    statCard: {
      draft: { headerBg: "#EDEEF1", iconBg: "#D8DBDF", accent: "#25272C" },
      submitted: { headerBg: "#D0EBFF", iconBg: "#AAD9FF", accent: "#0045FF" },
      completed: { headerBg: "#E3F5E3", iconBg: "#C8EAC9", accent: "#2D6830" },
      // Canceled appears only on the per-person drill-down (Figma node
      // 479:782, variables Red/100 + Red/200). Its card carries an Order
      // count and NO Sale Value — the frame has a single label slot and no
      // divider, unlike the other three.
      canceled: { headerBg: "#FFE3E1", iconBg: "#FFCCC8", accent: "#BE2217" },
      valueText: "#25272C",
      labelText: "#8E95A2",
      divider: "#EDEEF1",
    },
    // Data table (Figma "Refactor - 08/07/2026" > Customer List frame,
    // node 12266:13709) — header row text/background, row background,
    // row divider, and body cell text color shared by every restyled
    // list/table page (Customers, Orders, Catalog, Claims, Tiers).
    table: {
      headBg: "transparent",
      headText: "#25272C",
      // White, not the old #F7F8F8. The designer filed the grey THREE times —
      // Customer List (Essilor Issues 465:11966), Product Management
      // (470:12968) and Claim List (475:14705) — each saying the same thing:
      // "ทั้ง ๆ ที่ยังไม่ได้เกิดการ Hover หรือทำ Action อะไรกับ Row แต่ส่วนนี้เป็นสีเทา
      // ซึ่งปกติควรเป็นสีขาวปกติ". An idle row must not look interacted-with;
      // grey is reserved for the hover state MUI paints on its own.
      //
      // Those three lists are exactly the consumers of this token, so one edit
      // closes all three comments. The pinned/sticky cells in each list repaint
      // this same value, which is why it has to change HERE rather than
      // per-page: leave the token and the frozen columns keep the old grey
      // while the rest of the row goes white.
      rowBg: "#FFFFFF",
      rowDivider: "#D8DBDF",
      bodyText: "#25272C",
      mutedText: "#8E95A2",
    },
    // Customer Profile info card (Figma "Refactor - 08/07/2026" >
    // Customer List - Customer Profile Not approved frame, node
    // 12266:33823 "Customer Info") — label/value pair typography shared
    // by any detail-page 2-column info grid.
    infoField: {
      label: "#8E95A2",
      value: "#101828",
    },
    // File drop zone (Figma "Refactor - 08/07/2026" > Import Stock File
    // dialog, node 12266:162036 "Attach Picture More") — dashed-border
    // upload panel shared by any attach-file dialog.
    dropZone: {
      background: "#EDEEF1",
      border: "#6B7280",
      primaryText: "#5F6D78",
      hintText: "#9CADB6",
    },
  },
  radius: {
    field: 4,
    pill: 999,
  },
  font: {
    /*
     * Latin FIRST, then Thai — the opposite of apps/client, deliberately.
     *
     * Noto Sans Thai also ships Latin glyphs, so leading with it would silently
     * restyle every English label in the app. This app is English-primary
     * (`lang="en"`, English chrome) and Thai appears only inside data: store
     * names, contact names, and the defect reasons in @bolon/shared. Listing
     * Noto Sans first means Latin keeps its existing rendering and Thai falls
     * through to the next family that actually has the glyphs.
     *
     * apps/client is Thai-first (styles/base.css) because its copy is Thai.
     * Do not "align" the two orders — they differ for a reason.
     */
    family:
      "'Noto Sans', 'Noto Sans Thai', 'Roboto', 'Helvetica', 'Arial', sans-serif",
  },
} as const;

// Applied to /bolon/* by BolonLayout on top of the HOST theme (migration D6).
//
// Palette and typography come from the host — both apps use primary #111111 —
// so only the component behaviour the BOLON layouts were measured against
// lives here: pill buttons that neither wrap nor shrink, row Stacks that wrap,
// blue checkboxes and tab indicators, pale-tint filled Alerts, no scroll lock
// on popovers. MuiCssBaseline is deliberately NOT part of this set: CssBaseline
// is mounted once by the host root and a nested theme cannot reach it; it
// stays on the standalone `theme` below.
export const bolonComponentOverrides: NonNullable<ThemeOptions["components"]> =
  {
    MuiModal: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiDialog: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    // Every Bolon field is small (37px), the same height as the host's own
    // controls. A default here rather than `size="small"` on each field: the
    // source app sizes its fields per call site and often leaves the prop
    // out, so every sync would otherwise bring 40px fields back in. Scoped by
    // BolonLayout's nested ThemeProvider, so host pages are untouched.
    // Autocomplete needs its own default, because it passes its `size` to
    // `renderInput` explicitly, and an explicit prop beats a TextField default.
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: "small",
      },
    },
    MuiButton: {
      styleOverrides: {
        /*
         * Three rules that only matter on a NARROW screen, set here so all 41
         * files with buttons get them rather than each toolbar fixing itself.
         * Measured in Chromium at 360/390/430 before and after.
         *
         * `whiteSpace: nowrap` — the label must not wrap. In a fixed
         * `direction="row"` toolbar (46 files still use one) flexbox shrinks
         * every button to share the width, and at 360px a four-button row put
         * each one at 64px wide with its label broken across THREE lines: an
         * 86px-tall ellipse reading "EXPORT / EXCEL / FILE", with "ADD
         * PRODUC…" and "CANCEL" clipped out of the card. Refusing to wrap
         * makes the row overflow honestly instead, which the `minHeight`
         * below and a scrollable toolbar can then deal with.
         *
         * `minHeight: 44` ONLY ON A TOUCH DEVICE, via `(pointer: coarse)`.
         * The 44px minimum is a tap-target rule; this is a desktop admin app
         * driven with a mouse, where a 37px button is the correct, denser
         * size and forcing 44 everywhere just makes every toolbar taller for
         * no benefit. Applied unconditionally at first, which was wrong.
         *
         * `flexShrink: 0` — a button should keep its label's width and let
         * the ROW overflow, rather than each button silently compressing.
         * Without it the nowrap above just moves the clipping inside the
         * button instead of surfacing it.
         */
        root: {
          borderRadius: tokens.radius.pill,
          whiteSpace: "nowrap",
          flexShrink: 0,
          // A medium button is 36.5px (24.5px line + 5px padding + 1px
          // borders) beside 37.13px small inputs; at some zoom levels that
          // rounds to a visible 1px step. Pin it to the input height. The
          // touch rule below still wins on a coarse pointer.
          "&.MuiButton-sizeMedium": {
            minHeight: 37,
          },
          "@media (pointer: coarse)": {
            minHeight: 44,
          },
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: tokens.color.blackHover,
          },
        },
      },
    },
    /*
     * A horizontal Stack WRAPS instead of overflowing.
     *
     * This is the other half of the MuiButton rules above, and without it
     * those rules make things worse rather than better: once a button refuses
     * to shrink or wrap its label, a fixed `direction="row"` toolbar pushes
     * past the viewport and the whole PAGE scrolls sideways — measured at
     * 360/390/430, `documentElement.scrollWidth > innerWidth` on all three.
     * Wrapping keeps the buttons at their natural size and flows them onto as
     * many lines as they need: re-measured at 360px, no page overflow, no row
     * overflow, four buttons over three lines with every label readable.
     *
     * Scoped to `row` only. A COLUMN stack wrapping would be nonsense, and
     * most of this app's Stacks are columns doing ordinary layout.
     *
     * `useFlexGap` matters for the callsites that set `spacing`: MUI
     * implements spacing as margins by default, which do not collapse
     * correctly across a wrap and leave a doubled gap on the second line.
     * Most toolbars here already pass it; this rule is still correct without
     * it, just spaced slightly less evenly on the wrapped lines.
     */
    MuiStack: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const direction = ownerState?.direction;
          const isRow =
            direction === "row" ||
            (typeof direction === "object" &&
              direction !== null &&
              Object.values(direction).includes("row"));
          return isRow ? { flexWrap: "wrap" } : {};
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: tokens.color.link,
        },
      },
    },
    // App-wide so every checkbox agrees. Set here rather than per-callsite
    // because the checked colour had already drifted: MUI's default primary is
    // this app's black, so some boxes read black and any explicitly-styled one
    // read otherwise.
    MuiCheckbox: {
      styleOverrides: {
        root: {
          "&.Mui-checked, &.MuiCheckbox-indeterminate": {
            color: tokens.color.checkbox,
          },
        },
      },
    },
    // Tabs follow the checkbox blue, not the black primary (user call
    // 2026-09-04): with a black indicator the selected tab read as just
    // another bold label. Set here, like MuiCheckbox, so the three Tabs
    // surfaces (TierFormPage, CustomerTierPanel, achievement details) agree.
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: tokens.color.checkbox,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: tokens.color.checkbox,
          },
        },
      },
    },
    // Small Selects in the list toolbars render their placeholder through a
    // <Typography> and their value through a small <Chip>; both bring their own
    // font/height (16px/24px), so the Select stood 41px tall beside 37px text
    // fields (measured 2026-09-17 on customers, orders, claims and catalog).
    // Inherit the input font for the label and cap the chip at the input's
    // line height so every toolbar control shares one height. A bare <span>
    // from `renderValue` needs the same: One-Time Achievement's "All Months"
    // placeholder computed a 21.98px line height and stood 39px (2026-10-27).
    MuiSelect: {
      styleOverrides: {
        select: {
          "& > span, & .MuiTypography-root": {
            fontSize: "inherit",
            lineHeight: "inherit",
          },
          "& .MuiChip-root": {
            height: 20,
            fontSize: 12,
          },
        },
      },
    },
    // Breadcrumb trails read as a path, not a second heading: the links muted
    // grey, only the current page in text colour, and one step smaller than
    // the 20px page title beneath them (14px, the body2 size). Pages used
    // color="inherit" at 16px, which sat above the title as what looked like
    // the same heading twice.
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: 14,
          lineHeight: 1.43,
        },
        li: {
          "& .MuiTypography-root, & .MuiLink-root": {
            fontSize: "inherit",
            lineHeight: "inherit",
          },
          "& .MuiLink-root": {
            color: tokens.color.table.mutedText,
          },
        },
        separator: {
          fontSize: 14,
          marginLeft: 6,
          marginRight: 6,
        },
      },
    },
    MuiAlert: {
      variants: (
        Object.entries(tokens.color.toast) as Array<
          [
            keyof typeof tokens.color.toast,
            { background: string; text: string },
          ]
        >
      ).map(([severity, { background, text }]) => ({
        props: { variant: "filled" as const, severity },
        style: {
          backgroundColor: background,
          color: text,
          "& .MuiAlert-icon": {
            color: text,
          },
          "& .MuiAlert-action": {
            color: text,
          },
        },
      })),
    },
  };

// The standalone theme, kept for reference (and for any future test of the
// original app); the host app never mounts it — BolonLayout applies only
// `bolonComponentOverrides`.
export const theme = createTheme({
  palette: {
    primary: {
      main: tokens.color.black,
      dark: tokens.color.blackHover,
      contrastText: tokens.color.white,
    },
    text: {
      primary: tokens.color.textPrimary,
      secondary: tokens.color.textMuted,
    },
    divider: tokens.color.border,
  },
  typography: {
    fontFamily: tokens.font.family,
  },
  shape: {
    borderRadius: tokens.radius.field,
  },
  components: {
    /*
     * Stops the page shifting sideways when a dialog opens.
     *
     * MUI locks scrolling by putting `overflow: hidden` on <body>. Where the
     * scrollbar occupies layout width (Windows, and macOS set to "always show"
     * scroll bars) that removes the gutter, so every element jumps right by the
     * scrollbar's width and back again on close — reads as a flicker or lag.
     * MUI's own compensating padding is what `disableScrollLock` gives up, so
     * instead of disabling the lock we hold the gutter open with
     * `scrollbar-gutter`, which reserves the space whether or not a scrollbar
     * is drawn.
     *
     * Invisible on a default macOS setup (overlay scrollbars measure 0px), so
     * this is one to verify on Windows rather than here.
     */
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollbarGutter: "stable",
        },
      },
    },
    ...bolonComponentOverrides,
  },
});
